import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import { Link } from 'react-router';
import {
  CopyableValue,
  Disclosure,
  Field,
  Section,
} from '../../../components/common/detailParts';
import {
  actionLinkClassName,
  type AttentionItem,
  AttentionList,
  AttentionSummary,
  Tile,
} from '../../../components/common/detailChrome';
import {
  StatusBadge,
  TypedStatusBadge,
} from '../../../components/common/StatusBadge';
import { DappCategoryControl } from '../../../components/dapps/DappCategorySection';
import { DappPublishSection } from '../../../components/dapps/DappPublishSection';
import { S3Image } from '../../../components/S3Image';
import { formatRelativeTime } from '../../../lib/formatters';
import { Chip, Empty, Flag } from '../../../components/common/detailListParts';
import {
  deriveAttention,
  describeStoreStatus,
  releaseTicketPath,
  releaseVersion,
  STORE_LISTING_ANCHOR,
  ticketQueuePath,
  versionLabel,
} from '../dappSignals';
import type {
  SuperDappDetailData,
  SuperDappTabKey,
  SuperDappTabProps,
} from '../types';

const LINK_CLASS = 'text-teal-400 transition-colors hover:text-teal-300';

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function explorerAddress(address: string, cluster: string) {
  return `https://explorer.solana.com/address/${address}?cluster=${cluster}`;
}

function explorerTx(signature: string, cluster: string) {
  return `https://explorer.solana.com/tx/${signature}?cluster=${cluster}`;
}

/**
 * Left column: what state the app is in and the two actions admins actually
 * take here (category, publish). Right rail: who owns it and where it lives.
 */
export function OverviewTab(props: SuperDappTabProps) {
  const { data, store, storeStatus, isAdmin, formatDate, onTabChange } = props;
  const categories = store.info?.categories ?? [];
  const attention = deriveAttention(data, {
    storeStatus,
    categoryCount: categories.length,
    isAdmin,
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <div className="min-w-0 space-y-4">
        <PipelineCard
          data={data}
          storeStatus={storeStatus}
          categories={categories}
          attention={attention}
          onTabChange={onTabChange}
        />
        <StoreListingCard {...props} />
      </div>
      <aside className="min-w-0 space-y-4">
        <PublisherCard
          publisher={data.publisher}
          isAdmin={isAdmin}
          onTabChange={onTabChange}
        />
        <LinksCard dapp={data.dapp} />
        <DetailsCard dapp={data.dapp} formatDate={formatDate} />
        <OnChainCard data={data} formatDate={formatDate} />
      </aside>
    </div>
  );
}

function PipelineCard({
  data,
  storeStatus,
  categories,
  attention,
  onTabChange,
}: {
  data: SuperDappDetailData;
  storeStatus: SuperDappTabProps['storeStatus'];
  categories: string[];
  attention: AttentionItem<SuperDappTabKey>[];
  onTabChange: (tab: SuperDappTabKey) => void;
}) {
  const { dapp, releases, hubspotTickets } = data;
  const latest = releases[0];
  const latestTicketPath = latest
    ? releaseTicketPath(latest, hubspotTickets)
    : null;
  const hasPublishedVersion =
    dapp.publishedVersionName !== null || dapp.publishedVersionCode !== null;

  return (
    <Section
      title="Pipeline"
      trailing={<AttentionSummary count={attention.length} />}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Tile label="Portal">
          <div>
            <TypedStatusBadge type="dapp" status={dapp.status} />
          </div>
          <div className="text-xs text-neutral-500">
            {hasPublishedVersion
              ? `${versionLabel(dapp.publishedVersionName, dapp.publishedVersionCode)} live`
              : 'Nothing published'}
          </div>
        </Tile>

        <Tile label="dApp Store">
          <div>
            {storeStatus === undefined ? (
              <span
                aria-label="Loading store status"
                className="inline-block h-5 w-24 animate-pulse rounded-full bg-white/[0.06]"
              />
            ) : (
              <StatusBadge {...describeStoreStatus(storeStatus)} />
            )}
          </div>
          <div className="truncate text-xs text-neutral-500">
            {categories.length > 0
              ? categories.join(', ')
              : storeStatus
                ? 'No category'
                : 'Awaiting ingestion'}
          </div>
        </Tile>

        <Tile label="Latest release">
          {latest ? (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium tabular-nums text-white">
                  {releaseVersion(latest)}
                </span>
                <TypedStatusBadge type="release" status={latest.status} />
              </div>
              <div className="flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                <TypedStatusBadge
                  type="submission"
                  status={latest.submissionStatus}
                />
                <span title={formatRelativeTime(latest.created)}>
                  {formatRelativeTime(latest.created)}
                </span>
              </div>
            </>
          ) : (
            <span className="text-sm text-neutral-500">No releases yet</span>
          )}
        </Tile>
      </div>

      <AttentionList items={attention} onTabChange={onTabChange} />

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <button
          type="button"
          onClick={() => onTabChange('releases')}
          className={actionLinkClassName}
        >
          All releases →
        </button>
        {latestTicketPath && (
          <Link to={latestTicketPath} className={actionLinkClassName}>
            Latest review ticket →
          </Link>
        )}
        {dapp.packageName && (
          <Link
            to={ticketQueuePath({ search: dapp.packageName })}
            className={actionLinkClassName}
          >
            All tickets for this app →
          </Link>
        )}
      </div>
    </Section>
  );
}

function StoreListingCard({
  data,
  store,
  storeStatus,
  onStorePublished,
  canAssignCategories,
  canPublish,
}: SuperDappTabProps) {
  const { dapp } = data;

  return (
    <div id={STORE_LISTING_ANCHOR} className="scroll-mt-44">
      <Section
        title="Store listing"
        trailing={
          storeStatus !== undefined && (
            <StatusBadge {...describeStoreStatus(storeStatus)} />
          )
        }
      >
        <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
          <Field label="Store category">
            <DappCategoryControl canEdit={canAssignCategories} {...store} />
          </Field>
          <Field label="Languages">
            {dapp.languages.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {dapp.languages.map((language) => (
                  <Chip key={language} mono>
                    {language}
                  </Chip>
                ))}
              </div>
            ) : (
              <Empty />
            )}
          </Field>
        </div>

        {canPublish && dapp.status === 'Published' && dapp.packageName && (
          <DappPublishSection
            dappId={dapp.id}
            storeStatus={storeStatus}
            onPublished={onStorePublished}
          />
        )}

        <div className="mt-5 flex flex-col gap-5 border-t border-white/5 pt-5">
          <Field label="Subtitle">
            {dapp.subtitle ? (
              <span className="text-neutral-300">{dapp.subtitle}</span>
            ) : (
              <Empty />
            )}
          </Field>
          {dapp.editorsChoiceHeadline && (
            <Field label="Editor's choice">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-white/90">
                  {dapp.editorsChoiceHeadline}
                </span>
                {dapp.editorsChoiceTag && <Chip>{dapp.editorsChoiceTag}</Chip>}
              </div>
            </Field>
          )}
        </div>

        <Disclosure label="Description">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-300">
            {dapp.description || '—'}
          </p>
        </Disclosure>
      </Section>
    </div>
  );
}

function PublisherCard({
  publisher,
  isAdmin,
  onTabChange,
}: {
  publisher: SuperDappDetailData['publisher'];
  isAdmin: boolean;
  onTabChange: (tab: SuperDappTabKey) => void;
}) {
  const { organization, individual, kybSubmission, user } = publisher;
  const name = organization?.name ?? individual?.name ?? null;
  const website = organization?.website ?? individual?.website ?? null;
  const cluster = publisher.nftCluster || 'mainnet';
  const fallback = (
    <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ring-1 ring-white/10">
      <PersonIcon className="size-5 fill-current text-neutral-500" />
    </div>
  );

  return (
    <Section
      title="Publisher"
      trailing={
        <span className="text-xs text-neutral-500">{publisher.type}</span>
      }
    >
      <div className="flex items-center gap-3">
        {individual ? (
          <S3Image
            src={individual.avatarUrl}
            thumbnailSrc={individual.avatarThumbnailUrl}
            alt=""
            aria-hidden="true"
            className="size-11 shrink-0 rounded-xl object-cover ring-1 ring-white/10"
            fallback={fallback}
          />
        ) : (
          fallback
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-white">
            {name ?? 'Publisher'}
          </div>
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className={`${LINK_CLASS} block truncate text-xs`}
            >
              {hostnameOf(website)}
            </a>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        {kybSubmission && (
          <Field label="KYB">
            <TypedStatusBadge type="kyb" status={kybSubmission.status} />
          </Field>
        )}
        {isAdmin && user.email && (
          <Field label="Account">
            <CopyableValue value={user.email} mono={false} />
          </Field>
        )}
        {publisher.walletAddress && (
          <Field label="Wallet">
            <CopyableValue
              value={publisher.walletAddress}
              href={explorerAddress(publisher.walletAddress, cluster)}
            />
          </Field>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-white/5 pt-4 text-xs">
        <Link
          to={`/publishers/${publisher.id}`}
          className={actionLinkClassName}
        >
          Publisher page →
        </Link>
        {isAdmin && (
          <button
            type="button"
            onClick={() => onTabChange('publisher')}
            className={actionLinkClassName}
          >
            Edit details →
          </button>
        )}
      </div>
    </Section>
  );
}

function LinkRow({
  label,
  href,
  display,
}: {
  label: string;
  href: string;
  display: string;
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
      <span className="shrink-0 text-xs text-neutral-500">{label}</span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={href}
        className={`${LINK_CLASS} inline-flex min-w-0 items-center gap-1 text-sm`}
      >
        <span className="truncate">{display}</span>
        <OpenInNewIcon className="size-3 shrink-0 fill-current opacity-60" />
      </a>
    </li>
  );
}

function LinksCard({ dapp }: { dapp: SuperDappDetailData['dapp'] }) {
  const links = [
    dapp.appWebsite && {
      label: 'Website',
      href: dapp.appWebsite,
      display: hostnameOf(dapp.appWebsite),
    },
    dapp.contactEmail && {
      label: 'Contact',
      href: `mailto:${dapp.contactEmail}`,
      display: dapp.contactEmail,
    },
    dapp.supportEmail && {
      label: 'Support',
      href: `mailto:${dapp.supportEmail}`,
      display: dapp.supportEmail,
    },
    dapp.privacyPolicyUrl && {
      label: 'Privacy policy',
      href: dapp.privacyPolicyUrl,
      display: hostnameOf(dapp.privacyPolicyUrl),
    },
    dapp.licenseUrl && {
      label: 'License',
      href: dapp.licenseUrl,
      display: hostnameOf(dapp.licenseUrl),
    },
    dapp.copyrightUrl && {
      label: 'Copyright',
      href: dapp.copyrightUrl,
      display: hostnameOf(dapp.copyrightUrl),
    },
  ].filter((link): link is { label: string; href: string; display: string } =>
    Boolean(link),
  );

  return (
    <Section title="Links & contacts">
      {links.length === 0 ? (
        <Empty />
      ) : (
        <ul className="divide-y divide-white/5">
          {links.map((link) => (
            <LinkRow key={link.label} {...link} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function DetailsCard({
  dapp,
  formatDate,
}: {
  dapp: SuperDappDetailData['dapp'];
  formatDate: SuperDappTabProps['formatDate'];
}) {
  return (
    <Section title="Details">
      <div className="flex flex-col gap-4">
        <Field label="Package">
          {dapp.packageName ? (
            <CopyableValue value={dapp.packageName} />
          ) : (
            <Empty />
          )}
        </Field>
        {dapp.walletAddress && (
          <Field label="App wallet">
            <CopyableValue value={dapp.walletAddress} />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Token distribution">
            <Flag on={dapp.tokenDistribution} />
          </Field>
          <Field label="Imported from NFT">
            <Flag on={dapp.importedFromNft}>
              {dapp.importedFromNft ? 'Yes' : 'No'}
            </Flag>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Created">
            <span className="text-xs text-neutral-300">
              {formatDate(dapp.created)}
            </span>
          </Field>
          <Field label="Updated">
            <span className="text-xs text-neutral-300">
              {formatDate(dapp.updated)}
            </span>
          </Field>
        </div>
      </div>
    </Section>
  );
}

function OnChainCard({
  data,
  formatDate,
}: {
  data: SuperDappDetailData;
  formatDate: SuperDappTabProps['formatDate'];
}) {
  const { dapp, nfts } = data;
  const hasData =
    dapp.nftMintAddress ||
    dapp.nftTransactionSignature ||
    dapp.publisherNftMintAddress ||
    nfts.length > 0;

  if (!hasData) {
    return null;
  }

  const cluster = dapp.nftCluster || nfts[0]?.cluster || 'mainnet';

  return (
    <Section title="On-chain" trailing={<Chip>{cluster}</Chip>}>
      <div className="flex flex-col gap-4">
        {dapp.nftMintAddress && (
          <Field label="App NFT mint">
            <CopyableValue
              value={dapp.nftMintAddress}
              href={explorerAddress(dapp.nftMintAddress, cluster)}
            />
          </Field>
        )}
        {dapp.nftTransactionSignature && (
          <Field label="Mint transaction">
            <CopyableValue
              value={dapp.nftTransactionSignature}
              href={explorerTx(dapp.nftTransactionSignature, cluster)}
            />
          </Field>
        )}
        {dapp.nftMetadataUri && (
          <Field label="Metadata">
            <CopyableValue
              value={dapp.nftMetadataUri}
              href={dapp.nftMetadataUri}
            />
          </Field>
        )}
        {dapp.publisherNftMintAddress && (
          <Field label="Publisher NFT mint">
            <CopyableValue
              value={dapp.publisherNftMintAddress}
              href={explorerAddress(dapp.publisherNftMintAddress, cluster)}
            />
          </Field>
        )}
      </div>

      {nfts.length > 0 && (
        <Disclosure label="Minted NFTs" count={nfts.length}>
          <ul className="divide-y divide-white/5">
            {nfts.map((nft) => (
              <li key={nft.id} className="py-2.5 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Chip>{nft.type}</Chip>
                  <span className="min-w-0 truncate text-white/90">
                    {nft.name}
                  </span>
                  <span className="ml-auto text-xs text-neutral-500">
                    {formatDate(nft.mintedAt)}
                  </span>
                </div>
                <CopyableValue
                  value={nft.mintAddress}
                  href={explorerAddress(
                    nft.mintAddress,
                    nft.cluster || cluster,
                  )}
                  className="mt-1 text-xs"
                />
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
    </Section>
  );
}
